/**
 * Transport Cost Model — Phase 2 of Scenario Cost Analysis.
 * Light-duty fuel cost (gasoline vs electricity) and EV adoption capital cost.
 * Uses time series (baseline vs policy) and scenario; does not modify buildingsCostModel.
 *
 * Gradual adoption (timing):
 * - EV share (baseline and policy) is computed per year via ramp(); fuel savings use actual EV/ICE split each year.
 * - Annual capex = incremental new EVs that year × (EV_INCREMENTAL_COST + CHARGER_COST_PER_EV).
 *   Incremental new EVs in year t = increase in (policyEVShare − baselineEVShare) × vehicleCount from t−1 to t.
 * - cumulativeNetCostSeries = running sum of (capex − fuel savings). Payback = first year where cumulative ≤ 0.
 * - Curve: starts near zero, rises as capex is spent (early years), then falls as fuel savings dominate; crosses zero in 2030–2040 typically.
 */

import type { PolicyScenario } from './policyImpactService';
import type { BaselineProjections } from './policyBaselineCache';
import type { TimeSeriesEmissionData } from './policyTimeSeriesService';
import { computeTransportCostBetweenPathways, type TransportPathway } from './pathwayCostModel';

// ============== ASSUMPTIONS ==============
export const ICE_MPG = 25;
export const EV_KWH_PER_MILE = 0.30;
export const EV_INCREMENTAL_COST = 8000;
export const CHARGER_COST_PER_EV = 1000;
export const GASOLINE_PRICE_PER_GALLON_DEFAULT = 4.5;
export const DISCOUNT_RATE = 0.03;
const ANALYSIS_YEAR_MIN = 2023;
const ANALYSIS_YEAR_MAX = 2045;
/** Time series LDV emissions are in metric tons (MT); convert to kg for VMT/fuel math */
const LDV_MT_TO_KG = 1000;
/** kg CO2e per gallon gasoline (EPA) */
const KG_CO2E_PER_GALLON = 8.89;
export interface TransportCostOptions {
  gasolinePricePerGallon?: number;
  electricityPricePerKwh?: number;
  discountRate?: number;
  /** ICE fuel economy (mpg). */
  iceMpg?: number;
  /** EV efficiency (kWh per mile). */
  evKwhPerMile?: number;
  /** Incremental cost per EV vs ICE ($). */
  evIncrementalCost?: number;
  /** Charger cost per EV ($). */
  chargerCostPerEv?: number;
  /** Cost view: when bau_costs or total_costs, baseline = flat (no state policy). Used with emfacVersion for BAU transport upfront. */
  costView?: 'incremental_vs_bau' | 'bau_costs' | 'total_costs';
  /** EMFAC version: 2025 includes ZEV mandate (BAU has EVs vs flat); 2021 does not (BAU ≈ flat, $0 incremental). */
  emfacVersion?: '2021' | '2025';
}

export interface TransportCostResults {
  upfrontCapitalCost: number;
  annualFuelSavingsSeries: { year: number; value: number }[];
  /** Cumulative net cost by year (capex − savings); used for portfolio payback. */
  cumulativeNetCostSeries: { year: number; value: number }[];
  cumulativeNetCost: number;
  netPresentValue: number;
  emissionsReductionMT: number;
  costPerTon: number | null;
  paybackYear: number | null;
  breakdown: {
    vehicleCapex: number;
    chargingCost: number;
    /** Total fuel cost savings (gasoline + electricity, net) over 2023–2045 */
    fuelSavings: number;
    /** Gasoline cost savings only (baseline − policy gallons) × price, cumulative */
    gasolineSavings: number;
    /** Increase in electricity cost for EV charging (policy − baseline Kwh) × price when positive, cumulative */
    electricityCostIncrease: number;
  };
  /** Debug: location-specific inputs and outputs (baseline/policy VMT, EV share, upfront, reduction). */
  debug?: TransportCostDebug;
}

export interface TransportCostDebug {
  baselineVmt2023: number;
  policyVmt2023: number;
  baselineVmt2045: number;
  policyVmt2045: number;
  baselineEVShare2023: number;
  policyEVShare2023: number;
  baselineEVShare2045: number;
  policyEVShare2045: number;
  upfrontCapitalCost: number;
  emissionsReductionMT: number;
}

/** ICE-only emission factor (kg CO2/mile). LDV emissions are ICE-only since EV charging moved to electricity sector. */
function iceOnlyEmissionFactorKgPerMile(evShareFraction: number, iceMpg: number): number {
  const iceEf = KG_CO2E_PER_GALLON / iceMpg;
  const iceFrac = Math.max(0.001, Math.min(0.9999, 1 - evShareFraction)); // avoid 0
  return iceEf * iceFrac;
}

function ramp(year: number): number {
  if (year <= ANALYSIS_YEAR_MIN) return 0;
  if (year >= ANALYSIS_YEAR_MAX) return 1;
  return (year - ANALYSIS_YEAR_MIN) / (ANALYSIS_YEAR_MAX - ANALYSIS_YEAR_MIN);
}

/**
 * Compute transport cost results from baseline, time series (baseline vs policy), and scenario.
 * VMT and EV share are derived from LDV emissions and scenario targets.
 */
export function computeTransportCostResults(params: {
  baseline: BaselineProjections;
  timeSeries: { baselineData: TimeSeriesEmissionData[]; projectedData: TimeSeriesEmissionData[] };
  scenario: PolicyScenario;
  options?: TransportCostOptions;
}): TransportCostResults {
  const { baseline, timeSeries, scenario, options } = params;
  const gasolinePrice = options?.gasolinePricePerGallon ?? GASOLINE_PRICE_PER_GALLON_DEFAULT;
  const electricityPrice = options?.electricityPricePerKwh ?? 0.25;
  const discountRate = options?.discountRate ?? DISCOUNT_RATE;
  const iceMpg = Math.max(10, Math.min(60, options?.iceMpg ?? ICE_MPG));
  const evKwhPerMile = Math.max(0.2, Math.min(0.5, options?.evKwhPerMile ?? EV_KWH_PER_MILE));
  const evIncrementalCost = Math.max(0, options?.evIncrementalCost ?? EV_INCREMENTAL_COST);
  const chargerCostPerEv = Math.max(0, options?.chargerCostPerEv ?? CHARGER_COST_PER_EV);
  const costView = options?.costView ?? 'incremental_vs_bau';
  const emfacVersion = options?.emfacVersion ?? '2025';

  const baselineByYear = new Map(timeSeries.baselineData.map((d) => [d.year, d]));
  const projectedByYear = new Map(timeSeries.projectedData.map((d) => [d.year, d]));

  const years: number[] = [];
  for (let y = ANALYSIS_YEAR_MIN; y <= ANALYSIS_YEAR_MAX; y++) years.push(y);

  const bauEV2045 = Math.max(0, Math.min(100, baseline.currentEVPercent2045 ?? 20));

  // For incremental_vs_bau: scenario-based early return when no local policy (avoids race where time series
  // was generated with evAdoptionPercent:0 before sync to currentEVPercent; also when scenario matches BAU).
  if (costView === 'incremental_vs_bau') {
    const scenarioEV = Math.max(0, Math.min(100, scenario.evAdoptionPercent ?? 0));
    const evMatchesBau = Math.abs(scenarioEV - bauEV2045) < 0.5; // tight tolerance so 7.6% vs 6% BAU still computes capex
    const likelyPreSync = scenarioEV === 0 && bauEV2045 > 5;
    const noVmtReduction = (scenario.vmtReductionPercent ?? 0) < 0.1;
    const noHdv = (scenario.hdvElectrificationPercent ?? 0) < 0.1;
    if ((evMatchesBau || likelyPreSync) && noVmtReduction && noHdv) {
      const zeroSeries = years.map((y) => ({ year: y, value: 0 }));
      return {
        upfrontCapitalCost: 0,
        annualFuelSavingsSeries: zeroSeries,
        cumulativeNetCostSeries: zeroSeries,
        cumulativeNetCost: 0,
        netPresentValue: 0,
        emissionsReductionMT: 0,
        costPerTon: null,
        paybackYear: null,
        breakdown: {
          vehicleCapex: 0,
          chargingCost: 0,
          fuelSavings: 0,
          gasolineSavings: 0,
          electricityCostIncrease: 0,
        },
      };
    }
  }

  // For incremental_vs_bau: if LDV emissions are identical (no local transport policy), use BAU for both → $0 transport savings.
  const hasLocalTransportPolicy =
    costView !== 'incremental_vs_bau' ||
    years.some(
      (y) =>
        Math.abs((baselineByYear.get(y)?.lightDutyEmissions ?? 0) - (projectedByYear.get(y)?.lightDutyEmissions ?? 0)) >
        1e-6
    );

  // EV share: depends on cost view and EMFAC version.
  // - incremental_vs_bau: baseline = BAU; policy = scenario when local policy applied, else BAU → $0 when no sliders moved.
  // - bau_costs or total_costs + EMFAC 2025: flat = no ZEV mandate → baseline EV constant at 2023 level; policy (BAU) = ramp to currentEVPercent2045 → upfront and fuel savings.
  // - bau_costs or total_costs + EMFAC 2021: no ZEV in 2021 → baseline = policy = BAU EV% → $0 incremental.
  const isFlatVsBau = costView === 'bau_costs' || costView === 'total_costs';
  const bauEV2023 = baseline.currentEVPercent ?? 0;
  const scenarioEV2045 = Math.max(0, Math.min(100, scenario.evAdoptionPercent ?? 0));
  const policyEV2045 = hasLocalTransportPolicy ? scenarioEV2045 : bauEV2045;

  let baselineEV2023: number;
  let baselineEV2045: number;
  if (isFlatVsBau && emfacVersion === '2025') {
    baselineEV2023 = bauEV2023;
    baselineEV2045 = bauEV2023;
  } else if (isFlatVsBau && emfacVersion === '2021') {
    baselineEV2023 = bauEV2023;
    baselineEV2045 = bauEV2045;
  } else {
    // incremental_vs_bau: use actual BAU for baseline
    baselineEV2023 = bauEV2023;
    baselineEV2045 = bauEV2045;
  }

  // Per-year: EV shares, VMT, vehicle count, fuel costs (using actual EV/ICE split each year)
  const baselineEVShareByYear: number[] = [];
  const policyEVShareByYear: number[] = [];
  const baselineVmtByYear: number[] = [];
  const policyVmtByYear: number[] = [];
  const annualFuelCostBaseline: number[] = [];
  const annualFuelCostPolicy: number[] = [];

  for (let i = 0; i < years.length; i++) {
    const year = years[i];
    const r = ramp(year);
    const baselineEVShare = (baselineEV2023 + (baselineEV2045 - baselineEV2023) * r) / 100;
    const policyEVShare = (baselineEV2023 + (policyEV2045 - baselineEV2023) * r) / 100;

    const baseRow = baselineByYear.get(year);
    const projRow = projectedByYear.get(year);
    const baselineLdvKg = (baseRow?.lightDutyEmissions ?? 0) * LDV_MT_TO_KG;
    const policyLdvKg = (projRow?.lightDutyEmissions ?? 0) * LDV_MT_TO_KG;

    // LDV emissions are ICE-only (EV charging moved to electricity sector). VMT = LDV_kg / (ICE EF × (1 - evShare)).
    const baseEf = iceOnlyEmissionFactorKgPerMile(baselineEVShare, iceMpg);
    const policyEf = iceOnlyEmissionFactorKgPerMile(policyEVShare, iceMpg);

    const baselineVmt = baseEf > 0 ? baselineLdvKg / baseEf : 0;
    const policyVmt = policyEf > 0 ? policyLdvKg / policyEf : 0;

    baselineEVShareByYear.push(baselineEVShare);
    policyEVShareByYear.push(policyEVShare);
    baselineVmtByYear.push(baselineVmt);
    policyVmtByYear.push(policyVmt);

    const baselineGallons = baselineVmt * (1 - baselineEVShare) / iceMpg;
    const baselineKwh = baselineVmt * baselineEVShare * evKwhPerMile;
    const policyGallons = policyVmt * (1 - policyEVShare) / iceMpg;
    const policyKwh = policyVmt * policyEVShare * evKwhPerMile;

    const costBaseline = baselineGallons * gasolinePrice + baselineKwh * electricityPrice;
    const costPolicy = policyGallons * gasolinePrice + policyKwh * electricityPrice;

    annualFuelCostBaseline.push(costBaseline);
    annualFuelCostPolicy.push(costPolicy);
  }

  // Use evChargingKwh from time series when available (ensures cost matches GHG forecast)
  const baselineEvChargingKwhByYear = years.map((y) => baselineByYear.get(y)?.evChargingKwh ?? undefined);
  const policyEvChargingKwhByYear = years.map((y) => projectedByYear.get(y)?.evChargingKwh ?? undefined);
  const hasBaselineEvKwh = baselineEvChargingKwhByYear.every((v) => v !== undefined && v !== null);
  const hasPolicyEvKwh = policyEvChargingKwhByYear.every((v) => v !== undefined && v !== null);

  const baselinePathway: TransportPathway = {
    years,
    vmtByYear: baselineVmtByYear,
    evShareByYear: baselineEVShareByYear,
    ...(hasBaselineEvKwh && hasPolicyEvKwh && {
      evChargingKwhByYear: baselineEvChargingKwhByYear as number[],
    }),
  };
  const policyPathway: TransportPathway = {
    years,
    vmtByYear: policyVmtByYear,
    evShareByYear: policyEVShareByYear,
    ...(hasBaselineEvKwh && hasPolicyEvKwh && {
      evChargingKwhByYear: policyEvChargingKwhByYear as number[],
    }),
  };

  const pathwayResult = computeTransportCostBetweenPathways(baselinePathway, policyPathway, {
    gasolinePricePerGallon: gasolinePrice,
    electricityPricePerKwh: electricityPrice,
    discountRate,
    iceMpg,
    evKwhPerMile,
    evIncrementalCost,
    chargerCostPerEv,
  });

  const emissionsReductionMT = years.reduce((sum, year) => {
    const b = baselineByYear.get(year)?.lightDutyEmissions ?? 0;
    const p = projectedByYear.get(year)?.lightDutyEmissions ?? 0;
    return sum + Math.max(0, b - p);
  }, 0);
  const costPerTon =
    emissionsReductionMT > 0 && Number.isFinite(pathwayResult.cumulativeNetCost)
      ? pathwayResult.cumulativeNetCost / emissionsReductionMT
      : null;

  const cumulativeGasolineSavings = years.reduce((sum, _, i) => {
    const galSaved =
      baselineVmtByYear[i] * (1 - baselineEVShareByYear[i]) / iceMpg -
      policyVmtByYear[i] * (1 - policyEVShareByYear[i]) / iceMpg;
    return sum + Math.max(0, galSaved) * gasolinePrice;
  }, 0);
  const cumulativeElectricityCostIncrease = years.reduce((sum, _, i) => {
    const baselineKwh = hasBaselineEvKwh && hasPolicyEvKwh
      ? (baselineEvChargingKwhByYear[i] ?? 0)
      : baselineVmtByYear[i] * baselineEVShareByYear[i] * evKwhPerMile;
    const policyKwh = hasBaselineEvKwh && hasPolicyEvKwh
      ? (policyEvChargingKwhByYear[i] ?? 0)
      : policyVmtByYear[i] * policyEVShareByYear[i] * evKwhPerMile;
    const kwhIncrease = policyKwh - baselineKwh;
    return sum + Math.max(0, kwhIncrease) * electricityPrice;
  }, 0);

  const idx2045 = years.indexOf(ANALYSIS_YEAR_MAX);
  const idx2023 = years.indexOf(ANALYSIS_YEAR_MIN);
  const debug: TransportCostDebug = {
    baselineVmt2023: baselineVmtByYear[idx2023] ?? 0,
    policyVmt2023: policyVmtByYear[idx2023] ?? 0,
    baselineVmt2045: baselineVmtByYear[idx2045] ?? 0,
    policyVmt2045: policyVmtByYear[idx2045] ?? 0,
    baselineEVShare2023: (baselineEVShareByYear[idx2023] ?? 0) * 100,
    policyEVShare2023: (policyEVShareByYear[idx2023] ?? 0) * 100,
    baselineEVShare2045: (baselineEVShareByYear[idx2045] ?? 0) * 100,
    policyEVShare2045: (policyEVShareByYear[idx2045] ?? 0) * 100,
    upfrontCapitalCost: pathwayResult.upfrontCapitalCost,
    emissionsReductionMT,
  };

  return {
    upfrontCapitalCost: pathwayResult.upfrontCapitalCost,
    annualFuelSavingsSeries: pathwayResult.annualFuelSavingsSeries,
    cumulativeNetCostSeries: pathwayResult.cumulativeNetCostSeries,
    cumulativeNetCost: pathwayResult.cumulativeNetCost,
    netPresentValue: pathwayResult.netPresentValue,
    emissionsReductionMT,
    costPerTon,
    paybackYear: pathwayResult.paybackYear,
    breakdown: {
      vehicleCapex: pathwayResult.breakdown.vehicleCapex,
      chargingCost: pathwayResult.breakdown.chargingCost,
      fuelSavings: pathwayResult.breakdown.fuelSavings,
      gasolineSavings: cumulativeGasolineSavings,
      electricityCostIncrease: cumulativeElectricityCostIncrease,
    },
    debug,
  };
}
